import React, { useState, useEffect } from 'react';
import { Button, Skeleton } from '../../components/ui';
import { useLocation, useNavigate } from 'react-router-dom';
import { adminAPI } from '../../services/adminApi';
import SubNavigation from '../../components/admin/SubNavigation';
import {
  Analytics,
  Restaurant,
  People,
  LocalFireDepartment,
  Today,
  Category,
  Warning
} from '../../mui-icons';
import './MealsStatsPage.css';

const MealsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Determinar tab ativa baseado na URL
  const getActiveTab = () => {
    if (location.pathname === '/admin/meals/ingredients') return 'ingredients';
    return 'stats';
  };
  
  const [activeTab] = useState(getActiveTab());
  
  // Stats state
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Sub-navigation items
  const subNavItems = [
    { path: '/admin/meals', label: 'Estatísticas', icon: <Analytics sx={{ fontSize: 18 }} /> },
    { path: '/admin/ingredients', label: 'Alimentos', icon: <Restaurant sx={{ fontSize: 18 }} /> }
  ];

  useEffect(() => {
    if (activeTab === 'stats') {
      loadStats();
    }
  }, [activeTab]);

  const loadStats = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await adminAPI.getMealsStats();
      if (response.status === 'success') {
        setStats(response.data);
      }
    } catch (err) {
      console.error('Erro ao carregar estatísticas:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getDayLabel = (day) => {
    const days = {
      monday: 'Segunda',
      tuesday: 'Terça',
      wednesday: 'Quarta',
      thursday: 'Quinta',
      friday: 'Sexta',
      saturday: 'Sábado',
      sunday: 'Domingo'
    };
    return days[day] || day;
  };

  const getMaxMealsCount = () => {
    if (!stats?.meals_by_day) return 1;
    return Math.max(...stats.meals_by_day.map(d => d.count), 1);
  };

  // Loading state
  if (loading) {
    return (
      <div className="meals-stats-page">
        {/* Sub Navigation */}
        <SubNavigation items={subNavItems} />

        <div className="page-header">
          <Skeleton width={250} height={32} borderRadius={8} />
        </div>
        <div className="stats-grid">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <div key={i} className="admin-card pn-card pn-card--default">
              <Skeleton width="100%" height={100} borderRadius={8} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="meals-stats-page">
        {/* Sub Navigation */}
        <SubNavigation items={subNavItems} />

        <div className="page-header">
          <div className="page-title">
            <Analytics sx={{ fontSize: 32 }} />
            <div>
              <h1>Estatísticas de Refeições</h1>
              <p>Visão geral de alimentos e refeições</p>
            </div>
          </div>
        </div>
        <div className="admin-card pn-card pn-card--default">
          <div className="error-state">
            <Warning sx={{ fontSize: 48 }} />
            <p>{error}</p>
            <Button variant="primary" onClick={loadStats}>
              Tentar novamente
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="meals-stats-page">
      {/* Sub Navigation */}
      <SubNavigation items={subNavItems} />

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <Analytics sx={{ fontSize: 32 }} />
          <div>
            <h1>Estatísticas de Refeições</h1>
            <p>Visão geral de alimentos e refeições</p>
          </div>
        </div>
      </div>

      {/* Main Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card pn-card pn-card--stat">
          <div className="stat-icon ingredients">
            <Restaurant />
          </div>
          <div className="stat-content">
            <span className="stat-value">{stats?.ingredients?.total?.toLocaleString() || 0}</span>
            <span className="stat-label">Alimentos no Catálogo</span>
            <span className="stat-sub">{stats?.ingredients?.categories || 0} categorias</span>
          </div>
        </div>

        <div className="stat-card pn-card pn-card--stat">
          <div className="stat-icon meals">
            <LocalFireDepartment />
          </div>
          <div className="stat-content">
            <span className="stat-value">{stats?.meals?.total?.toLocaleString() || 0}</span>
            <span className="stat-label">Refeições Cadastradas</span>
            <span className="stat-sub">Média: {stats?.meals?.avg_calories?.toFixed(0) || 0} kcal</span>
          </div>
        </div>

        <div className="stat-card pn-card pn-card--stat">
          <div className="stat-icon users">
            <People />
          </div>
          <div className="stat-content">
            <span className="stat-value">{stats?.meals?.users_with_meals?.toLocaleString() || 0}</span>
            <span className="stat-label">Usuários com Refeições</span>
          </div>
        </div>

        <div className="stat-card pn-card pn-card--stat">
          <div className="stat-icon consumption">
            <Today />
          </div>
          <div className="stat-content">
            <span className="stat-value">{stats?.consumption?.consumed_today?.toLocaleString() || 0}</span>
            <span className="stat-label">Consumidas Hoje</span>
            <span className="stat-sub">{stats?.consumption?.total_records?.toLocaleString() || 0} total</span>
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="charts-row">
        {/* Meals by Day Chart */}
        <div className="admin-card pn-card pn-card--default chart-card">
          <h3>
            <Today sx={{ fontSize: 20 }} />
            Refeições por Dia da Semana
          </h3>
          <div className="bar-chart">
            {stats?.meals_by_day?.map(day => (
              <div key={day.week_day} className="bar-item">
                <div className="bar-container">
                  <div 
                    className="bar-fill"
                    style={{ height: `${(day.count / getMaxMealsCount()) * 100}%` }}
                  >
                    <span className="bar-value">{day.count.toLocaleString()}</span>
                  </div>
                </div>
                <span className="bar-label">{getDayLabel(day.week_day)}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Top Categories */}
        <div className="admin-card pn-card pn-card--default chart-card">
          <h3>
            <Category sx={{ fontSize: 20 }} />
            Top Categorias de Alimentos
          </h3>
          <div className="categories-list">
            {stats?.top_categories?.map((cat, index) => (
              <div key={cat.category} className="category-item">
                <div className="category-rank">{index + 1}</div>
                <div className="category-info">
                  <span className="category-name">{cat.category}</span>
                  <div className="category-bar-container">
                    <div 
                      className="category-bar-fill"
                      style={{ 
                        width: `${(cat.count / (stats.top_categories[0]?.count || 1)) * 100}%` 
                      }}
                    ></div>
                  </div>
                </div>
                <span className="category-count">{cat.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Users with Consumption */}
      <div className="admin-card pn-card pn-card--default">
        <h3>
          <People sx={{ fontSize: 20 }} />
          Resumo de Consumo
        </h3>
        <div className="consumption-summary">
          <div className="consumption-item">
            <span className="consumption-label">Total de Registros de Consumo</span>
            <span className="consumption-value">{stats?.consumption?.total_records?.toLocaleString() || 0}</span>
          </div>
          <div className="consumption-item">
            <span className="consumption-label">Usuários com Histórico</span>
            <span className="consumption-value">{stats?.consumption?.users_with_consumption?.toLocaleString() || 0}</span>
          </div>
          <div className="consumption-item highlight">
            <span className="consumption-label">Refeições Consumidas Hoje</span>
            <span className="consumption-value">{stats?.consumption?.consumed_today?.toLocaleString() || 0}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MealsPage;

